import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from '@sentry/react';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer role check with setTimeout
        if (session?.user) {
          setTimeout(() => {
            checkUserAccess(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
          setIsModerator(false);
          setIsMaster(false);
          setIsActive(true);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        checkUserAccess(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUserAccess = async (userId: string) => {
    try {
      const { data: isMasterData } = await (supabase as any).rpc('is_master');
      setIsMaster(!!isMasterData);

      const { data: isAdminData } = await (supabase as any).rpc('is_admin');
      setIsAdmin(!!isAdminData);

      const { data: isModeratorData } = await (supabase as any).rpc('is_moderator');
      setIsModerator(!!isModeratorData);

      Sentry.setUser({ id: userId });

      // Check if user is active - usar maybeSingle() ao invés de single()
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('ativo')
        .eq('id', userId)
        .maybeSingle(); // maybeSingle() retorna null se não encontrar, não lança erro
      
      // Se não encontrou o profile, tentar criar um
      if (profileError || !profile) {
        console.warn('Profile not found, attempting to create:', profileError);
        
        // Tentar criar o profile se não existir
        const { error: createError } = await supabase
          .from('profiles')
          .upsert({
            id: userId,
            ativo: true
          }, {
            onConflict: 'id'
          });
        
        if (createError) {
          console.error('Error creating profile:', createError);
          setIsActive(false);
          return;
        }
        
        // Se criou com sucesso, definir como ativo
        setIsActive(true);
        return;
      }
      
      setIsActive(profile.ativo ?? true);

      // If user is not active, sign them out
      if (profile && !profile.ativo) {
        await supabase.auth.signOut();
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      Sentry.captureException(error, { tags: { context: 'checkUserAccess' } });
      setIsAdmin(false);
      setIsModerator(false);
      setIsMaster(false);
      setIsActive(false);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setIsAdmin(false);
      setIsModerator(false);
      setIsMaster(false);
      setIsActive(true);
      Sentry.setUser(null);
    }
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/admin/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return {
    user,
    session,
    isAdmin,
    isModerator,
    isMaster,
    isActive,
    loading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  };
};
